import { times } from 'ramda';

type DecimalSeparatorValue = 0 | 1 | 'Dot' | 'Comma';
type ThousandsSeparatorValue = 0 | 1 | 2 | 'Comma' | 'Dot' | 'Space';

const decimalSeparatorValues = {
  Dot: 0,
  Comma: 1,
} as const;

const thousandsSeparatorValues = {
  Comma: 0,
  Dot: 1,
  Space: 2,
} as const;

export const getRandomInt = (min: number, max: number) => {
  const minCeil = Math.ceil(min);
  const maxFloor = Math.floor(max);
  return Math.floor(Math.random() * (maxFloor - minCeil + 1)) + minCeil;
};

export const getSignString = (num: number) => {
  const sign = Math.sign(num);

  if (sign === 0) {
    return '';
  }

  return sign === 1 ? '+' : '-';
};

export const padNumber = (number: number, requiredDigits: number, padVal = '0') => {
  const digits = number !== 0 ? Math.floor(Math.log10(number)) + 1 : 1;

  const pad = requiredDigits - digits;

  let paddedNumber = `${number}`;
  if (pad > 0) {
    // eslint-disable-next-line no-return-assign
    times(() => paddedNumber = padVal + paddedNumber, pad);
  }

  return paddedNumber;
};

export const round = (number: number, decimalPlaces: number = 2) => Math.round((number + Number.EPSILON) * (10 ** decimalPlaces)) / (10 ** decimalPlaces);

// Unlike ramda's clamp, never throws on an inverted range (bad stored limits); max wins.
export const clampToRange = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

export const nearestTen = (num: number) => Math.ceil(num / 10) * 10;
export const isNumber = (value: any): value is number => typeof value === 'number' && Number.isFinite(value);

const getDecimalSeparatorToken = (decimalSeparator?: DecimalSeparatorValue) => (
  decimalSeparator === decimalSeparatorValues.Comma || decimalSeparator === 'Comma'
    ? ','
    : '.'
);
const getThousandsSeparatorToken = (thousandsSeparator?: ThousandsSeparatorValue) => {
  switch (thousandsSeparator) {
    case thousandsSeparatorValues.Dot:
    case 'Dot':
      return '.';
    case thousandsSeparatorValues.Space:
    case 'Space':
      return ' ';
    default:
      return ',';
  }
};

export const formatProjectNumber = (
  value: number | undefined,
  decimalPlaces: number,
  decimalSeparator?: DecimalSeparatorValue,
  thousandsSeparator?: ThousandsSeparatorValue,
) => {
  if (value === undefined || !Number.isFinite(value)) {
    return '';
  }

  const rounded = round(value, decimalPlaces);
  const fixed = rounded.toFixed(decimalPlaces);
  const [integerPart, fractionPart] = fixed.split('.');
  const groupSeparator = getThousandsSeparatorToken(thousandsSeparator);
  const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, groupSeparator);

  if (decimalPlaces <= 0) {
    return formattedInteger;
  }

  return `${formattedInteger}${getDecimalSeparatorToken(decimalSeparator)}${fractionPart}`;
};

export const createProjectNumberFormatter = (
  decimalSeparator?: DecimalSeparatorValue,
  thousandsSeparator?: ThousandsSeparatorValue,
) => (value: number | undefined, decimalPlaces: number) => formatProjectNumber(
  value,
  decimalPlaces,
  decimalSeparator,
  thousandsSeparator,
);

export const parseProjectNumber = (
  value: string,
  decimalSeparator?: DecimalSeparatorValue,
  thousandsSeparator?: ThousandsSeparatorValue,
) => {
  if (!value?.trim()) {
    return undefined;
  }

  const decimalToken = getDecimalSeparatorToken(decimalSeparator);
  const thousandsToken = getThousandsSeparatorToken(thousandsSeparator);
  let normalised = value.trim().replace(/\s+/g, ' ');

  if (decimalToken === thousandsToken) {
    const lastIndex = normalised.lastIndexOf(decimalToken);
    const digitsOnly = normalised.split(decimalToken).join('');
    if (lastIndex >= 0) {
      const decimals = normalised.length - lastIndex - 1;
      if (decimals > 0 && digitsOnly.length > decimals) {
        normalised = `${digitsOnly.slice(0, -decimals)}.${digitsOnly.slice(-decimals)}`;
      } else {
        normalised = digitsOnly;
      }
    } else {
      normalised = digitsOnly;
    }
  } else {
    normalised = normalised.split(thousandsToken).join('');
    normalised = normalised.split(decimalToken).join('.');
  }

  const parsed = Number(normalised);
  return Number.isFinite(parsed) ? parsed : undefined;
};

export const createProjectNumberParser = (
  decimalSeparator?: DecimalSeparatorValue,
  thousandsSeparator?: ThousandsSeparatorValue,
) => (value: string) => parseProjectNumber(
  value,
  decimalSeparator,
  thousandsSeparator,
);
